import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import {
  Sparkles,
  ShieldCheck,
  Zap,
  Check,
  CalendarDays,
  Timer,
  Users,
  ArrowRight,
} from "lucide-react";

function Pricing() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const tiers = [
    {
      id: "normal-1",
      title: "Découverte",
      subtitle: "Pour tester sans engagement",
      price: "165€",
      priceNote: "HT / session",
      totalNote: "165€ HT au total",
      packType: "normal",
      sessions: 1,
      badge: "1 session",
      highlight: false,
      validity: "Journée",
      validityIcon: <Timer className="h-4 w-4 text-indigo-500" />,
      validityNote: "Jouable le jour choisi, jusqu'à 23:59",
      features: [
        "4 à 6 joueurs",
        "Accès instantané après paiement",
        "Facture automatique",
        "Support e-mail",
      ],
    },
    {
      id: "normal-5",
      title: "Pack 5 sessions",
      subtitle: "Le choix le plus populaire",
      price: "145€",
      priceNote: "HT / session",
      totalNote: "725€ HT au total (5 × 145€)",
      packType: "normal",
      sessions: 5,
      badge: "Le plus populaire",
      highlight: true,
      validity: "Journée",
      validityIcon: <Timer className="h-4 w-4 text-indigo-300" />,
      validityNote: "Chaque session jouable le jour choisi",
      savings: "−20€/session vs découverte",
      features: [
        "4 à 6 joueurs par session",
        "5 sessions indépendantes",
        "Tarif dégressif dès 5 sessions",
        "Planifiez chaque session à la date qui vous convient",
        "Facture automatique",
        "Support prioritaire",
      ],
    },
    {
      id: "extended-1",
      title: "Semaine flexible",
      subtitle: "Quand caler un créneau est compliqué",
      price: "185€",
      priceNote: "HT / session",
      totalNote: "185€ HT au total",
      packType: "extended",
      sessions: 1,
      badge: "Flexible",
      highlight: false,
      validity: "7 jours",
      validityIcon: <CalendarDays className="h-4 w-4 text-orange-500" />,
      validityNote: "Activable une fois, jouable sur 7 jours consécutifs",
      features: [
        "4 à 6 joueurs",
        "Aucun stress de planning : jouez quand vous êtes prêts",
        "Créateur d'équipe inclus",
        "Accès instantané après paiement",
        "Facture automatique",
      ],
    },
  ];

  const handleCTA = (t) => {
    const params = new URLSearchParams({
      packType: t.packType,
      sessions: t.sessions,
    }).toString();
    if (token) {
      navigate(`/catalogue?${params}`);
    } else {
      navigate(`/signup?from=pricing&${params}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              "radial-gradient(circle at 12% 0%, rgba(99,102,241,0.18) 0, transparent 55%), radial-gradient(circle at 88% 10%, rgba(249,115,22,0.14) 0, transparent 55%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.09]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(15,23,42,0.10) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.10) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage: "radial-gradient(circle at 50% 10%, black 0%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(circle at 50% 10%, black 0%, transparent 70%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 py-12 sm:py-16 text-center">
          <p className="text-[11px] tracking-[0.22em] uppercase text-slate-400 mb-3">
            Tarifs &amp; packs
          </p>
          <h1 className="text-3xl sm:text-4xl font-semibold mb-4">
            Des packs simples, des prix clairs
          </h1>
          <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
            Achetez une ou plusieurs sessions, planifiez-les à votre rythme. Pas d'abonnement, pas de surprise.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-2 text-[12px] text-slate-600">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1">
              <Zap className="h-4 w-4 text-orange-500" />
              Accès instantané
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1">
              <ShieldCheck className="h-4 w-4 text-indigo-600" />
              Paiement sécurisé Stripe
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1">
              <Users className="h-4 w-4 text-slate-600" />
              4 à 6 joueurs par session
            </span>
          </div>
        </div>
      </section>

      {/* OFFRES */}
      <section className="max-w-7xl mx-auto px-6 py-10 grid gap-8 sm:gap-10 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {tiers.map((t) => (
          <div
            key={t.id}
            className={[
              "relative flex flex-col rounded-2xl p-6 bg-white border shadow-sm transition-all duration-300",
              "hover:shadow-md hover:-translate-y-0.5",
              t.highlight
                ? "border-indigo-400/70 ring-1 ring-indigo-100 shadow-[0_12px_35px_-25px_rgba(99,102,241,0.55)]"
                : "border-slate-200",
            ].join(" ")}
          >
            {t.highlight && (
              <div
                className="pointer-events-none absolute -inset-0.5 rounded-2xl opacity-60"
                style={{
                  background:
                    "radial-gradient(circle at 20% 0%, rgba(99,102,241,0.22) 0, transparent 55%), radial-gradient(circle at 95% 30%, rgba(249,115,22,0.16) 0, transparent 65%)",
                }}
              />
            )}

            <div className="relative flex flex-col flex-1">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{t.title}</h3>
                  <p className="text-sm text-slate-500 mt-0.5">{t.subtitle}</p>
                </div>
                <span
                  className={[
                    "shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border",
                    t.highlight
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                      : "bg-slate-50 text-slate-700 border-slate-200",
                  ].join(" ")}
                >
                  {t.highlight && <Sparkles className="h-3.5 w-3.5 text-orange-200" />}
                  {t.badge}
                </span>
              </div>

              {/* Prix */}
              <div className="mt-6 flex items-baseline gap-2">
                <span className={["text-3xl font-extrabold", t.highlight ? "text-indigo-600" : "text-slate-900"].join(" ")}>
                  {t.price}
                </span>
                <span className="text-sm text-slate-500">{t.priceNote}</span>
              </div>
              <div className="mt-1 text-[12px] font-semibold text-slate-700">{t.totalNote}</div>
              {t.savings && (
                <div className="mt-1 text-[11px] text-orange-600 font-medium">{t.savings}</div>
              )}

              {/* Validité */}
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-[12px] text-slate-700 w-fit max-w-full">
                <span className="shrink-0 mt-0.5">{t.validityIcon}</span>
                <div>
                  <span className="font-semibold text-slate-800">{t.validity}</span>
                  <span className="mx-1.5 text-slate-300">·</span>
                  <span className="text-slate-600">{t.validityNote}</span>
                </div>
              </div>

              {/* Features */}
              <ul className="mt-6 space-y-2 text-sm text-slate-700 flex-1">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span
                      className={[
                        "mt-0.5 shrink-0 inline-flex h-5 w-5 items-center justify-center rounded-full border",
                        t.highlight ? "border-indigo-200 bg-indigo-50" : "border-slate-200 bg-slate-50",
                      ].join(" ")}
                    >
                      <Check className={["h-3.5 w-3.5", t.highlight ? "text-indigo-600" : "text-slate-600"].join(" ")} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-8">
                <button
                  onClick={() => handleCTA(t)}
                  className={[
                    "w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200",
                    t.highlight
                      ? "bg-indigo-600 text-white hover:bg-indigo-500 shadow-sm"
                      : "bg-slate-900 text-white hover:bg-indigo-600 shadow-sm",
                  ].join(" ")}
                >
                  {token ? "Réserver maintenant" : "Créer un compte"}
                </button>
                {!token && (
                  <p className="mt-3 text-sm text-slate-500 text-center">
                    Déjà un compte ?{" "}
                    <button
                      onClick={() => {
                        const params = new URLSearchParams({ from: "pricing", packType: t.packType, sessions: t.sessions }).toString();
                        navigate(`/login?${params}`);
                      }}
                      className="text-indigo-600 hover:text-indigo-500 underline"
                    >
                      Connectez-vous
                    </button>
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Tarifs dégressifs */}
      <section className="max-w-5xl mx-auto px-6 pb-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400 mb-4">Tarifs dégressifs</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-slate-700">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="pb-2 text-left font-medium text-slate-500">Sessions</th>
                  <th className="pb-2 text-center font-medium text-slate-500">Journée</th>
                  <th className="pb-2 text-center font-medium text-slate-500">Semaine flexible</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {[
                  { label: "1 session", normal: "165€", extended: "185€" },
                  { label: "2 à 4 sessions", normal: "155€", extended: "170€" },
                  { label: "5 à 9 sessions", normal: "145€", extended: "160€" },
                  { label: "10 sessions et +", normal: "135€", extended: "150€" },
                ].map((row) => (
                  <tr key={row.label}>
                    <td className="py-2.5 font-medium text-slate-800">{row.label}</td>
                    <td className="py-2.5 text-center text-slate-700">{row.normal} HT / session</td>
                    <td className="py-2.5 text-center text-slate-700">{row.extended} HT / session</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-[11px] text-slate-400">
            Les paliers sont calculés par commande. Chaque session est indépendante et planifiable à la date de votre choix.
          </p>
        </div>
      </section>

      {/* Infos légales */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
          <div
            className="pointer-events-none absolute inset-0 opacity-60"
            style={{
              background:
                "radial-gradient(circle at 10% 0%, rgba(99,102,241,0.10) 0, transparent 55%), radial-gradient(circle at 95% 30%, rgba(249,115,22,0.08) 0, transparent 65%)",
            }}
          />
          <div className="relative">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400 mb-3">Informations</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
                <span>
                  Chaque session accueille <span className="font-medium text-slate-800">4 à 6 joueurs</span>. Le jeu est conçu pour fonctionner avec un minimum de 4 joueurs.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-400" />
                <span>
                  <span className="font-medium text-slate-800">Journée</span> = valable jusqu'à 23:59 du jour choisi. <span className="font-medium text-slate-800">Semaine flexible</span> = activable une fois, jouable pendant 7 jours consécutifs (jour d'activation inclus).
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                <span>
                  Paiement sécurisé via Stripe. Une facture est générée automatiquement après chaque achat.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Pricing;